<template>
  <div class="app" :class="{ 'loading': isLoading }">
    <InitialLanguageModal 
      v-if="showLanguageModal" 
      :show="showLanguageModal"
      @close="hideLanguageModal"
    />
    <!-- 添加SEO管理组件 -->
    <SeoManager />
    <NavBar v-if="!hideNavbarRoutes" />
    <main class="main-content" :class="{ 'no-nav': hideNavbarRoutes }">
      <router-view v-slot="{ Component }">
        <transition name="page" mode="out-in">
          <component :is="Component" />
        </transition>
      </router-view>
    </main>
    <PWAInstallManager />
    <footer v-if="!hideNavbarRoutes" class="footer">
      <div class="footer-content">
        <div class="footer-main">
          <div class="footer-logo">
            <img src="/icons/icon.svg" alt="WhisperHerb Logo" class="logo-image">
            <span class="logo-text">{{ appName }}</span>
          </div>
          <p class="footer-tagline">{{ $t('app.tagline') }}</p>
        </div>
        <div class="footer-nav">
          <div class="footer-nav-section">
            <h4>{{ $t('footer.features') }}</h4>
            <router-link to="/analyze" class="footer-link">{{ $t('footer.plant_analysis') }}</router-link>
            <router-link to="/my-plants" class="footer-link">{{ $t('footer.plant_collection') }}</router-link>
            <a href="#" class="footer-link">{{ $t('footer.care_guides') }}</a>
          </div>
          <div class="footer-nav-section">
            <h4>{{ $t('footer.company') }}</h4>
            <router-link to="/about" class="footer-link">{{ $t('footer.about_us') }}</router-link>
            <router-link to="/contact" class="footer-link">{{ $t('footer.contact') }}</router-link>
          </div>
          <div class="footer-nav-section">
            <h4>{{ $t('footer.legal') }}</h4>
            <router-link to="/privacy-policy" class="footer-link">{{ $t('footer.privacy_policy') }}</router-link>
            <router-link to="/terms-of-use" class="footer-link">{{ $t('footer.terms_of_use') }}</router-link>
          </div>
        </div>
      </div>
      <div class="footer-bottom">
        <p>{{ $t('footer.copyright', { year: currentYear }) }}</p>
        <LanguageSelector class="language-selector-container" />
      </div>
    </footer>
    
    <!-- Scroll to top button -->
    <button 
      v-show="showScrollTop" 
      @click="scrollToTop" 
      class="scroll-top-button"
      aria-label="Scroll to top"
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <polyline points="18 15 12 9 6 15"></polyline>
      </svg>
    </button>

    <!-- Loading overlay -->
    <Transition name="fade">
      <div v-if="isLoading" class="loading-overlay">
        <div class="loading-content">
          <div class="loading-spinner"></div>
          <p>{{ loadingMessage }}</p>
        </div>
      </div>
    </Transition>

    <!-- GDPR Consent Modal -->
    <ConsentModal @consent-saved="handleConsentSaved" />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, provide } from 'vue';
import { useHead } from '@vueuse/head';
import NavBar from './components/NavBar.vue';
import { useAuthStore } from './stores/authStore';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import LanguageSelector from './components/LanguageSelector.vue';
import ConsentModal from './components/ConsentModal.vue';
import InitialLanguageModal from './components/InitialLanguageModal.vue';
import { logger } from './utils/logger';
import PWAInstallManager from './components/PWAInstallManager.vue';
import SeoManager from './components/SeoManager.vue';

const authStore = useAuthStore();
const router = useRouter();
const route = useRoute();
const { t } = useI18n();

// Add computed property for app name with fallback
const appName = computed(() => {
  try {
    return t('app.name') || 'WhisperHerb';
  } catch (error) {
    logger.error('Error getting app name:', error);
    return 'WhisperHerb';
  }
});

const currentYear = computed(() => new Date().getFullYear());

// 导入SEO标签管理组合式函数
import { useSeoTags } from './composables/useSeoTags';

// 使用SEO标签管理
useSeoTags();

// Default SEO Meta Tags
const appTagline = computed(() => t('app.tagline') || 'Your personal plant care assistant.');
useHead({
  title: computed(() => appName.value), // Default title using app name
  meta: [
    {
      name: 'description',
      content: computed(() => appTagline.value), // Default description using tagline
    },
    // 添加关键词
    {
      name: 'keywords',
      content: computed(() => 'plant care, plant identification, plant disease, plant health, AI plant care, plant analysis, ' + appName.value),
    },
    // 添加作者
    {
      name: 'author',
      content: computed(() => appName.value),
    },
  ],
  // You can also add link tags, script tags etc. here globally
  script: [
    {
      type: 'application/ld+json',
      innerHTML: computed(() => JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Organization',
        'name': appName.value,
        'url': window.location.origin,
        'logo': `${window.location.origin}/icons/icon-512x512.png`,
        'description': appTagline.value,
        'sameAs': [
          'https://twitter.com/whisperherb', // 如果有社交媒体账号，请替换为实际链接
          'https://facebook.com/whisperherb',
          'https://instagram.com/whisperherb'
        ],
        'contactPoint': {
          '@type': 'ContactPoint',
          'telephone': '+1-123-456-7890', // 替换为实际联系电话
          'contactType': 'customer service',
          'availableLanguage': ['English', '中文', 'Français', 'Deutsch', 'Nederlands', 'Español']
        },
        'potentialAction': {
          '@type': 'SearchAction',
          'target': {
            '@type': 'EntryPoint',
            'urlTemplate': `${window.location.origin}/analyze?q={search_term_string}`
          },
          'query-input': 'required name=search_term_string'
        }
      }))
    },
  ],
});
const showScrollTop = ref(false);
const isLoading = ref(false);
const loadingMessage = ref(t('common.loading'));
const hasConsent = ref(false);
const showCookieSettings = ref(false);
const showLanguageModal = ref(false);

const hideNavbarRoutes = computed(() => {
  // Hide navbar for chat routes and plant details routes
  return route.name === 'plant-chat' || route.name === 'plant-details';
});

// Provide loading methods to child components
provide('setLoading', (loading, message = t('common.loading')) => {
  isLoading.value = loading;
  loadingMessage.value = message;
});

const checkScroll = () => {
  showScrollTop.value = window.scrollY > 500;
};

const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    behavior: 'smooth'
  });
};

// Handle cookie consent saved
const handleConsentSaved = (consents) => {
  logger.debug('Consent saved:', consents);
  localStorage.setItem('gdprConsent', JSON.stringify(consents));
};

// Initialize authentication when app starts
onMounted(async () => {
  logger.debug('App mounted, initializing auth');
  
  try {
    const success = await authStore.initAuth();
    logger.debug(`Auth initialization from App.vue ${success ? 'successful' : 'failed'}`);
    if (!success && authStore.token) {
      logger.warn('Auth initialization failed but token exists - may be invalid token');
    }
  } catch (error) {
    logger.error('Error during App auth initialization:', error);
  }
  
  // Check if consent was already given
  const savedConsent = localStorage.getItem('gdprConsent');
  if (savedConsent) {
    hasConsent.value = true;
  }
  
  // 检查是否已经选择过语言
  const hasSelectedLanguage = localStorage.getItem('language_selected');
  if (!hasSelectedLanguage) {
    showLanguageModal.value = true;
  }
  
  // Add scroll event listener
  window.addEventListener('scroll', checkScroll);
  
  // Router navigation guards for loading state
  router.beforeEach((to, from, next) => {
    // Only show loading for route changes that require data fetching
    if (to.meta.requiresAuth) {
      isLoading.value = true;
      loadingMessage.value = t('common.loading_page');
    }
    next();
  });
  
  router.afterEach((to) => {
    // Add a small delay to prevent flash
    setTimeout(() => {
      isLoading.value = false;
    }, 200);
    
    // Log navigation completion
    logger.debug(`Navigation completed to ${to.path}`);
  });
  
  router.onError((error) => {
    logger.error('Navigation error:', error);
    isLoading.value = false;
  });
});

const hideLanguageModal = () => {
  showLanguageModal.value = false;
};

onUnmounted(() => {
  window.removeEventListener('scroll', checkScroll);
});
</script>

<style>
:root {
  /* Color System */
  --primary-color: #2E7D32;      /* 更深的绿色，提高对比度 */
  --primary-dark: #1B5E20;       /* 更深的背景色 */
  --primary-light: #A5D6A7;      /* 稍微深一点的浅绿色 */
  --secondary-color: #455A64;    /* 更深的蓝灰色 */
  --secondary-dark: #263238;     /* 近黑色的深蓝灰 */
  --secondary-light: #B0BEC5;    /* 更深的浅蓝灰 */
  
  /* Accent Colors */
  --accent-color: #FF6F00;       /* 更深的橙色 */
  --accent-dark: #E65100;        /* 深橙色 */
  --accent-light: #FFB74D;       /* 浅橙色 */
  
  /* Semantic Colors */
  --success-color: #2E7D32;      /* 同主色 */
  --error-color: #D32F2F;        /* 更深的红色 */
  --warning-color: #F57C00;      /* 更深的橙色 */
  --info-color: #1976D2;         /* 更深的蓝色 */
  
  /* Text Colors */
  --text-color: #212121;         /* 近黑色文本 */
  --text-secondary: #424242;     /* 深灰色次要文本 */
  --text-light: #757575;         /* 中灰色文本 */
  --text-inverse: #FFFFFF;       /* 白色文本 */
  
  /* Background Colors */
  --bg-color: #FFFFFF;
  --bg-light: #F5F5F5;
  --bg-dark: #E0E0E0;
  --bg-surface: #FFFFFF;
  
  /* Border Colors */
  --border-color: #BDBDBD;       /* 更深的边框色 */
  --border-light: #E0E0E0;
  --border-dark: #9E9E9E;
  
  /* Shadow Variables */
  --shadow-sm: 0 1px 3px rgba(0, 0, 0, 0.12);
  --shadow-md: 0 4px 6px rgba(0, 0, 0, 0.1);
  --shadow-lg: 0 10px 15px rgba(0, 0, 0, 0.1);
  --shadow-xl: 0 20px 25px rgba(0, 0, 0, 0.1);
  
  /* Border Radius */
  --radius-sm: 4px;
  --radius-md: 8px;
  --radius-lg: 12px;
  --radius-xl: 20px;
  --radius-full: 9999px;
  
  /* Spacing System */
  --space-2xs: 0.25rem;  /* 4px */
  --space-xs: 0.5rem;    /* 8px */
  --space-sm: 0.75rem;   /* 12px */
  --space-md: 1rem;      /* 16px */
  --space-lg: 1.5rem;    /* 24px */
  --space-xl: 2rem;      /* 32px */
  --space-2xl: 3rem;     /* 48px */
  --space-3xl: 4rem;     /* 64px */
  
  /* Container Widths */
  --container-sm: 640px;
  --container-md: 768px;
  --container-lg: 1024px;
  --container-xl: 1280px;
  
  /* Typography */
  --font-family: 'Inter', system-ui, -apple-system, BlinkMacSystemFont, sans-serif;
  --font-size-xs: 0.75rem;   /* 12px */
  --font-size-sm: 0.875rem;  /* 14px */
  --font-size-md: 1rem;      /* 16px */
  --font-size-lg: 1.125rem;  /* 18px */
  --font-size-xl: 1.25rem;   /* 20px */
  --font-size-2xl: 1.5rem;   /* 24px */
  --font-size-3xl: 2rem;     /* 32px */
  
  /* Line Heights */
  --line-height-tight: 1.2;
  --line-height-normal: 1.5;
  --line-height-loose: 1.8;
  
  /* Font Weights */
  --font-weight-light: 300;
  --font-weight-normal: 400;
  --font-weight-medium: 500;
  --font-weight-semibold: 600;
  --font-weight-bold: 700;
  
  /* Transitions */
  --transition-fast: 150ms ease;
  --transition-normal: 250ms ease;
  --transition-slow: 350ms ease;
  
  /* Component Specific */
  --header-height: 64px;
  --footer-height: 60px;
  --disabled-color: #9E9E9E;
}

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: var(--font-family);
  line-height: var(--line-height-normal);
  color: var(--text-color);
  background-color: var(--bg-light);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  scroll-behavior: smooth;
}

.app {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

.main-content {
  flex: 1;
  padding-bottom: var(--space-2xl);
}

.container {
  width: 100%;
  max-width: var(--container-xl);
  margin: 0 auto;
  padding: 0 var(--space-md);
}

/* Button styles */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-sm) var(--space-lg);
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-medium);
  text-decoration: none;
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: all var(--transition-normal);
  border: none;
  color: var(--text-inverse);
  background-color: var(--primary-color);
}

.btn:hover {
  background-color: var(--primary-dark);
  transform: translateY(-2px);
  box-shadow: var(--shadow-md);
}

.btn:active {
  transform: translateY(0);
}

.btn-outline {
  background-color: transparent;
  border: 1px solid var(--primary-color);
  color: var(--primary-color);
}

.btn-outline:hover {
  background-color: var(--primary-light);
  color: var(--primary-dark);
}

.btn-secondary {
  background-color: var(--secondary-color);
}

.btn-secondary:hover {
  background-color: var(--secondary-dark);
}

.btn-lg {
  padding: var(--space-md) var(--space-xl);
  font-size: var(--font-size-lg);
}

.btn-sm {
  padding: var(--space-xs) var(--space-md);
  font-size: var(--font-size-sm);
}

/* Card styles */
.card {
  background-color: var(--bg-color);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-md);
  overflow: hidden;
  transition: transform var(--transition-normal), box-shadow var(--transition-normal);
}

.card:hover {
  box-shadow: var(--shadow-lg);
}

/* Footer styles */
.footer {
  background-color: var(--primary-dark);
  color: white;
  padding-top: var(--space-lg);
}

.footer-content {
  max-width: var(--container-xl);
  margin: 0 auto;
  padding: 0 var(--space-lg);
  display: grid;
  grid-template-columns: minmax(250px, 1fr) auto;
  gap: var(--space-xl);
  align-items: start;
}

.footer-main {
  display: flex;
  flex-direction: column;
}

.footer-logo {
  display: flex;
  align-items: center;
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-bold);
  margin-bottom: var(--space-xs);
}

.logo-image {
  width: auto;
  height: 24px;
  margin-right: var(--space-xs);
}

.footer-tagline {
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: var(--space-md);
  max-width: 300px;
  font-size: var(--font-size-sm);
}

.footer-nav {
  display: grid;
  grid-template-columns: repeat(3, auto);
  gap: var(--space-xl);
}

.footer-nav-section {
  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
}

.footer-nav-section h4 {
  color: white;
  margin-bottom: var(--space-xs);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.footer-link {
  color: rgba(255, 255, 255, 0.85);
  text-decoration: none;
  transition: color var(--transition-fast);
  font-size: var(--font-size-sm);
  padding: var(--space-2xs) 0;
}

.footer-link:hover {
  color: white;
  text-decoration: underline;
}

.footer-bottom {
  padding: var(--space-md) var(--space-lg);
  border-top: 1px solid rgba(255, 255, 255, 0.2);
  margin-top: var(--space-lg);
  color: rgba(255, 255, 255, 0.9);
  font-size: 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--space-md);
  text-align: center;
}

.language-selector-container {
  margin: var(--space-xs) 0;
}

/* Existing language selector styles */
.language-selector {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.language-select {
  padding: 0.5rem;
  border-radius: var(--radius-md);
  border: 1px solid rgba(255, 255, 255, 0.3);
  background-color: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 0.875rem;
  cursor: pointer;
}

label {
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.9);
  font-weight: var(--font-weight-medium);
}

/* Scroll to top button */
.scroll-top-button {
  position: fixed;
  bottom: var(--space-lg);
  right: var(--space-lg);
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background-color: var(--primary-color);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  cursor: pointer;
  z-index: 99;
  box-shadow: var(--shadow-md);
  transition: transform var(--transition-normal), background-color var(--transition-normal);
  opacity: 0.9;
}

.scroll-top-button:hover {
  transform: translateY(-5px);
  background-color: var(--primary-dark);
  opacity: 1;
}

/* Page transition animations */
.page-enter-active,
.page-leave-active {
  transition: opacity 0.3s, transform 0.3s;
}

.page-enter-from {
  opacity: 0;
  transform: translateY(20px);
}

.page-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}

/* Responsive utility classes */
.d-none {
  display: none !important;
}

.d-flex {
  display: flex !important;
}

.flex-column {
  flex-direction: column !important;
}

.align-center {
  align-items: center !important;
}

.justify-center {
  justify-content: center !important;
}

.text-center {
  text-align: center !important;
}

.w-100 {
  width: 100% !important;
}

.h-100 {
  height: 100% !important;
}

/* Typography */
h1, h2, h3, h4, h5, h6 {
  line-height: var(--line-height-tight);
  margin-bottom: var(--space-md);
}

h1 {
  font-size: var(--font-size-3xl);
  font-weight: var(--font-weight-bold);
}

h2 {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
}

h3 {
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-semibold);
}

p {
  margin-bottom: var(--space-md);
}

a {
  color: var(--primary-color);
  text-decoration: none;
  transition: color var(--transition-fast);
}

a:hover {
  color: var(--primary-dark);
}

/* Animation classes */
.fade-in {
  animation: fadeIn 0.5s ease-in-out;
}

.slide-up {
  animation: slideUp 0.5s ease-in-out;
}

.slide-in-right {
  animation: slideInRight 0.5s ease-in-out;
}

.scale-in {
  animation: scaleIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideUp {
  from { transform: translateY(20px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}

@keyframes slideInRight {
  from { transform: translateX(20px); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
}

@keyframes scaleIn {
  from { transform: scale(0.95); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}

@media (max-width: 768px) {
  h1 {
    font-size: calc(var(--font-size-3xl) * 0.9);
  }
  
  h2 {
    font-size: calc(var(--font-size-2xl) * 0.9);
  }
  
  h3 {
    font-size: calc(var(--font-size-xl) * 0.9);
  }
  
  .container {
    padding: 0 var(--space-sm);
  }
  
  .footer-content {
    grid-template-columns: 1fr;
    gap: var(--space-lg);
  }
  
  .footer-nav {
    grid-template-columns: repeat(3, 1fr);
    gap: var(--space-md);
  }
  
  .footer-bottom {
    padding: var(--space-lg) var(--space-md);
  }
  
  .language-selector-container {
    margin-left: 0;
  }
  
  .scroll-top-button {
    bottom: var(--space-md);
    right: var(--space-md);
    width: 40px;
    height: 40px;
  }
}

@media (max-width: 480px) {
  h1 {
    font-size: calc(var(--font-size-3xl) * 0.8);
  }
  
  h2 {
    font-size: calc(var(--font-size-2xl) * 0.8);
  }
  
  h3 {
    font-size: calc(var(--font-size-xl) * 0.8);
  }
  
  .footer-nav {
    grid-template-columns: repeat(2, 1fr);
    gap: var(--space-md);
  }
  
  .footer-tagline {
    margin-bottom: var(--space-sm);
  }
  
  .scroll-top-button {
    bottom: var(--space-sm);
    right: var(--space-sm);
  }
}

/* Loading overlay */
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: white;
  padding: var(--space-xl);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  max-width: 85%;
  text-align: center;
}

.loading-spinner {
  width: 50px;
  height: 50px;
  border: 4px solid var(--primary-light);
  border-top: 4px solid var(--primary-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: var(--space-md);
}

.loading-content p {
  margin: 0;
  color: var(--text-color);
  font-weight: var(--font-weight-medium);
}

/* Fade transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.main-content.no-nav {
  padding: 0;
  margin: 0;
  min-height: 100vh;
}
</style>